import { useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Search from "../../../../components/form/Search";
import Button from "../../../../components/form/Button";
import Table from "../../../../components/elements/table/Table";
import { decrypt, encrypt, handleFilterChange } from "../../../../helpers";
import DeleteModal from "../../../../components/common/DeleteModal";
import { useDebounce } from "../../../../hooks/useDebounce";
import LimitSelect from "../../../../components/elements/LimitSelect";
import { logInAsHandler, userDelete, userList, userStatusChange } from "../../../../services/userService";
import Switch from "../../../../components/form/Switch";
import Badge from "../../../../components/elements/Badge";
import { classNames } from "../../../../helpers/classNames";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ManageUserDrawer from "../../../../components/sections/admin/user/drawer/ManageUserDrawer";
import ActionBar from "../../../../components/elements/ActionBar";
import { tableFilter } from "../../../../helpers/tableFilterOptions";
import Select from "../../../../components/form/select/Select";
import { useDispatch } from "react-redux";
import { setSession } from "../../../../redux/slice/authSlice";
import { setProfile } from "../../../../redux/slice/profileSlice";
import { apiRequest } from "../../../../util/apiUtils";

const Users = () => {
  const { session, loginData } = useSelector((state) => state?.authProvider);
  const queryClient = useQueryClient();
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  const { lang } = useSelector((state) => state?.langProvider);

  const { data, isFetching, isLoading, error } = useQuery({
    queryKey: ["user", filters, search],
    queryFn: async () => {
      const { role = [], statusCode = [], ...restFilters } = filters || {};

      const queryParams = {
        ...restFilters,
        ...(search && { keyword: search }),
        ...Object.fromEntries([...role].map((val, i) => [`role[${i}]`, val])),
        ...Object.fromEntries([...statusCode].map((val, i) => [`statusCode[${i}]`, val])),
      };

      const res = await userList(queryParams);
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs || {};
    },
  });

  const options = [
    { id: "email", icon: "fa-regular fa-envelope", tooltip: "Resend Email" },
    {
      id: "userDelete",
      icon: "fa-regular fa-trash-can",
      tooltip: "User Delete",
    },
  ];

  // add or edit user =================>
  function userManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          queryClient.setQueryData(["user", filters, search], (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = [res?.data, ...stateData];
              return { ...state, data: stateData };
            }
          });
          if (data?.metadata?.totalDocs > filters?.limit) {
            queryClient.invalidateQueries(["user", filters, search]);
          }
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }
      if (res?.status === 200) {
        queryClient.setQueryData(["user", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = stateData?.map((item) => (item?._id === res?.data?._id ? { ...item, ...res?.data } : item));
            return { ...state, data: stateData };
          }
        });
        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["user", filters, search]);
        }
      }
      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  // const { mutate: statusChangeHandler, isPending: statusChangeInPending } =
  //   useMutation({
  //     mutationFn: async ({ id, status }) => {
  //       queryClient.setQueryData(["user", filters, search], (state) => {
  //         if (state) {
  //           let stateData = JSON.parse(JSON.stringify(state?.data));
  //           stateData = (stateData || []).map((elm) =>
  //             elm?._id === id
  //               ? { ...elm, status: elm?.status === 1 ? 2 : 1 }
  //               : elm
  //           );
  //           return { ...state, data: stateData };
  //         }
  //       });
  //       const res = await userStatusChange({
  //         ids: id,
  //         status: status,
  //       });
  //       return { res, payload: { id, status } };
  //     },
  //     onSuccess: ({ res, payload }) => {
  //       if (res?.status !== 200) {
  //         queryClient.setQueryData(["user", filters, search], (state) => {
  //           if (state) {
  //             let stateData = JSON.parse(JSON.stringify(state?.data));
  //             stateData = (stateData || []).map((elm) =>
  //               elm?._id === payload?.id
  //                 ? { ...elm, status: elm?.status === 1 ? 2 : 1 }
  //                 : elm
  //             );
  //             return { ...state, data: stateData };
  //           }
  //         });
  //       }
  //     },
  //     onError: (error) => {
  //       console.log(error);
  //     },
  //   });

  const navigate = useNavigate();

  const { mutate: statusChangeHandler, isPending: statusChangeInPending } = useMutation({
    mutationFn: async ({ id, status }) => {
      queryClient.setQueryData(["user", filters, search], (state) => {
        if (state) {
          let stateData = JSON.parse(JSON.stringify(state?.data));
          stateData = (stateData || []).map((elm) => (elm?._id === id ? { ...elm, status: elm?.status === "active" ? "inactive" : "active" } : elm));
          return { ...state, data: stateData };
        }
      });

      const res = await userStatusChange({ ids: id, status });
      return { res, payload: { id, status } };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status !== 200) {
        queryClient.setQueryData(["user", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = (stateData || []).map((elm) =>
              elm?._id === payload?.id ? { ...elm, status: elm?.status === "active" ? "inactive" : "active" } : elm
            );
            return { ...state, data: stateData };
          }
        });
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const tableHeadData = [
    {
      _id: 1,
      name: "User Name",
      align: "left",
    },
    {
      _id: 2,
      name: "Email",
      align: "left",
    },
    // {
    //   _id: "language",
    //   name: "Language",
    //   align: "left",
    // },
    {
      _id: 3,
      name: "Role",
      align: "left",
      isFilter: true,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0) {
          setFilters((pre) => ({
            ...pre,
            role: val?.map((item) => item?.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            delete pre?.role;
            return { ...pre };
          });
        }
      },
      filterData: [
        { name: "User", value: "user", type: "checkbox" },
        { name: "Admin", value: "admin", type: "checkbox" },
        { name: "Organization", value: "organization", type: "checkbox" },
      ],
    },
    // {
    //   _id: 4,
    //   name: "Last Login",
    //   align: "left",
    // },

    // {
    //   _id: "loginMethod",
    //   name: "Login Method",
    //   align: "left",
    // },
    {
      _id: "status",
      name: "Status",
      align: "left",
      ...tableFilter({
        filterKey: "status",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val }));
        },
        filterList: [
          { name: "pending", value: "pending", type: "checkbox" },
          { name: "active", value: "active", type: "checkbox" },
          { name: "invited", value: "invited", type: "checkbox" },
          { name: "deactivated", value: "deactivated", type: "checkbox" },
          { name: "deleted", value: "deleted", type: "checkbox" },
          { name: "inactive", value: "inactive", type: "checkbox" },
        ],
      }),
    },
    {
      _id: 7,
      name: "Action",
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "user",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: classNames(item?.firstName, item?.lastName) || "-",
          image: item?.image?.url,
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.email || "-",
        },
        // {
        //   _id: "language",
        //   type: "text",
        //   align: "left",
        //   cellClass: "!min-w-[100px]",
        //   data: item?.language?.name || "-",
        // },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.role || "-",
        },
        // {
        //   _id: 4,
        //   type: "text",
        //   align: "left",
        //   cellClass: "!min-w-[100px]",
        //   data: item?.lastLogin
        //     ? dayjs(item?.lastLogin).format("DD MMM, YYYY")
        //     : "-",
        // },
        // {
        //   _id: "loginMethod",
        //   type: "text",
        //   align: "left",
        //   cellClass: "!min-w-[100px]",
        //   data: item?.mainLogin || "-",
        // },
        {
          _id: "status",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div className="flex gap-2 items-center">
              <Switch
                className={"!w-10 !h-5 before:!w-4 before:!h-4 peer-checked:before:!left-[22px]"}
                checked={item?.status === "active"}
                onChange={() => {
                  statusChangeHandler({
                    id: item?._id,
                    status: item?.status === "active" ? "inactive" : "active",
                  });
                }}
              />
              <Badge type={item?.status === "active" ? "success" : "danger"} label={item?.status === "active" ? "Active" : "Inactive"} />
            </div>
          ),
        },
        {
          _id: 6,
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: 1,
              name: "Log In As",
              icon: "fa-regular fa-key",
              onClick: () => {
                handleLogInAs({
                  encodedPayload: encrypt({
                    text: JSON.stringify({
                      _id: item?._id,
                      uid: item?.uid,
                      email: item?.email,
                    }),
                    secretKey: "STACSEKORABRITEWE34",
                  }),
                });
              },
              standout: false,
            },
            {
              _id: 2,
              name: "Edit",
              icon: "fa-regular fa-pen-to-square",
              onClick: () => {
                setModalOpen({ isOpen: true, data: item });
              },
              standout: false,
            },
            {
              _id: 3,
              name: "Delete",
              icon: "fa-regular fa-trash-can",
              standout: true,
              onClick: () => {
                setDeleteModal({
                  isOpen: true,
                  data: item,
                });
              },
            },
          ],
        },
      ],
    };
  });

  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await userDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return {
        res,
        payload: Array.isArray(payload) ? payload : [payload],
      };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        queryClient.setQueryData(["user", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = (stateData || [])?.filter((item) => !payload?.includes(item?._id));
            return { ...state, data: stateData };
          }
        });

        queryClient.invalidateQueries(["user", filters, search]);
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  function actionHandler(val) {
    switch (val) {
      case "userDelete":
        setDeleteModal({
          isOpen: true,
          data: { _id: selectedItems },
        });
        break;

      default:
        break;
    }
  }

  const dispatch = useDispatch();

  const { mutate: handleLogInAs, isPending: loggingInAs } = useMutation({
    mutationFn: async (payload) => {
      const res = await logInAsHandler(payload);
      return { res, payload };
    },
    onSuccess: async ({ res, payload }) => {
      if (res?.status === 200 && res?.accessToken && res?.refreshToken) {
        localStorage.setItem("original_admin_access_token", session?.user?.access_token);
        localStorage.setItem("original_admin_refresh_token", localStorage.getItem("refreshToken"));
        localStorage.setItem("auth_token", res.accessToken);
        localStorage.setItem("refreshToken", res.refreshToken);

        let decryptedData = {};
        try {
          decryptedData = JSON.parse(decrypt(payload.encodedPayload, "STACSEKORABRITEWE34"));
          if (decryptedData?._id) {
            localStorage.setItem("userId", decryptedData._id);
          }
        } catch (err) {
          console.warn("Failed to decrypt payload:", err);
        }

        const verify = await apiRequest("post", `verify-token`, { body: {} }, "application/json", () => {}, {
          Authorization: "Bearer " + res?.accessToken,
        });

        if (verify?.data?.status === 200) {
          const user = verify?.data?.data || {};

          dispatch(setProfile({ ...user, loading: false, access_token: res.accessToken }));
          dispatch(
            setSession({
              user: { ...user, access_token: res.accessToken },
              status: "authorized",
              error: null,
            })
          );

          toast.success(res.message || "Logged in as user successfully");

          setTimeout(() => {
            navigate(`/${lang}/user/events`, { replace: true });

            navigate(`/${lang}`);

            navigate(`/${lang}/profile`);
          }, 300);
        } else {
          toast.error("Token verification failed");
        }
      } else {
        toast.warning(res?.message || "Login as user failed");
      }
    },

    onError: (error) => {
      toast.error(error?.message || "Something went wrong");
    },
  });

  return (
    <div className="w-full flex flex-col pb-10 bg-white rounded-md">
      <div className="space-y-8 py-8 px-5">
        <div className="flex justify-between items-center border-b border-gray-300">
          <div className="flex flex-col gap-2 w-full">
            <div className="font-bold text-lg xl:text-2xl uppercase tracking-tighter font-Montserrat">User Management</div>
            <div className="text-sm xl:text-base h-10">Easily handle all user actions: update user info, email, status, and add new user data.</div>
          </div>
          {/* <div className="relative">
            <Button
              variant="secondary"
              buttonLabel={"Create New"}
              buttonHasLink
              buttonFunction={() => setModalOpen({ isOpen: true, data: null })}
            />
          </div> */}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-36">
              <LimitSelect
                value={filters?.limit}
                allValue={data?.metadata?.totalDocs}
                onChange={(val) => handleFilterChange("limit", val, setFilters)}
              />
            </div>
            <div className="w-72">
              <Search
                placeholder={"Search here"}
                search={(val) => {
                  setKeyword(val);
                  handleFilterChange("page", 1, setFilters);
                }}
              />
            </div>
            {selectedItems?.length > 0 && (
              <div className="flex items-center gap-2">
                <ActionBar options={options} onChange={actionHandler} />
                <span className="flex items-center rounded-full bg-secondary text-surface-01 text-xs px-3 py-1 gap-2">
                  {selectedItems?.length} Selected
                  <button onClick={() => setSelectedItems([])} type="button" className="cursor-pointer">
                    <i className="fa-fw fa-light fa-xmark"></i>
                  </button>
                </span>
              </div>
            )}
          </div>
          {/* <div className="flex items-center gap-3">
            <div className="relative">
              <Select
                selectedValue={"Buyer"}
                dropdownData={[{ name: "Buyer", value: "Buyer" }]}
                onChange={(val) => {
                  console.log({ val });
                  handleChange({ target: { name: "parent", value: val } });
                }}
                onScroll={(e) => {
                  loadData(e, categories, setCategoryilters);
                }}
              />
            </div>
            <div className="relative">
              <Select
                selectedValue={"Past 3 month"}
                dropdownData={[{ name: "Past 3 month", value: "Past 3 month" }]}
                // onChange={(val) => {
                //   console.log({ val });
                //   handleChange({ target: { name: "parent", value: val } });
                // }}
                // onScroll={(e) => {
                //   loadData(e, categories, setCategoryilters);
                // }}
              />
            </div>
          </div> */}
        </div>
        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          isLoder={isLoading}
          pageCount={data?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
            setSelectedItems([]);
          }}
          handleOrdering={(val) => {
            console.log(val);
          }}
          isFetching={isFetching}
          isOrderingDisabled={true}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          tableActionDropdownContainer={"!w-48"}
          // selectable
        />
        <ManageUserDrawer
          open={modalOpen?.isOpen}
          data={modalOpen?.data}
          close={() => setModalOpen({ isOpen: false, data: null })}
          getResponse={userManageResponse}
        />
        <DeleteModal
          open={deleteModal?.isOpen}
          loading={deletePending}
          onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
          deleteHandler={() => {
            deleteHandler(deleteModal?.data?._id);
          }}
        />
      </div>
    </div>
  );
};

export default Users;
